import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { componentsFactory, type ReadyToShareData, useActionsFactory } from '../factories';
import type { SharingModalProps } from '../types';
import { getFullPostTypeForTracking } from '../utils/fullPostType';

type HookReturnType = {
	data: ReturnType<typeof componentsFactory>;
	actions: ReturnType<typeof useActionsFactory> & {
		trackView: () => void;
	};
};

export const VIEW_EVENT_NAME = 'petition_manage_ready_to_share_post_modal_view';

export function useSharingModal(props: SharingModalProps, dynamicData: ReadyToShareData): HookReturnType {
	const track = useTracking();
	const { primaryAction, secondaryAction } = useActionsFactory(props, dynamicData);
	const components = useCallback(() => {
		return componentsFactory(props, dynamicData);
	}, [props, dynamicData]);

	const actions = useCallback(() => {
		return {
			primaryAction,
			secondaryAction,
		};
	}, [primaryAction, secondaryAction]);

	const recommendedGroup = 'recommendedGroup' in props ? props.recommendedGroup : undefined;
	const { platform, postType } = props;

	const trackView = useCallback(() => {
		const { context, viewContext } = props.trackingProps;
		const location = getFullPostTypeForTracking(platform, postType, !!recommendedGroup);

		void track(VIEW_EVENT_NAME, {
			context,
			view_context: viewContext,
			location,
			content: 'show_draft_post',
		});
	}, [props.trackingProps, platform, postType, recommendedGroup, track]);

	const { isOpen } = props;

	useEffect(() => {
		if (isOpen) {
			trackView();
		}
	}, [isOpen, trackView]);

	return {
		data: {
			...components(),
		},
		actions: {
			...actions(),
			trackView,
		},
	};
}
